/**
 * AI 对话相关 API 服务
 */
import apiClient from './client';
import type { PaginatedResponse } from '@/types/api';
import type {
  ConversationCreateRequest,
  ConversationDetail,
  ConversationGenerateResult,
  ConversationMidjourneySeparateResult,
  ConversationListParams,
  GeneratePromptRequest,
  GeneratePromptResponse,
  ConversationMessage,
  ConversationMessageListParams,
  ConversationMessagesBatchParams,
  ConversationMessagesBatchResponse,
  ConversationMessageWithdrawResult,
  ConversationSendMessageRequest,
  ConversationSummary,
  ConversationTriggerGenerateRequest,
} from '@/types/ai-conversation';
import type {
  MentionCandidateScope,
  MentionResourceType,
  MentionableResourcesResponse,
} from '@/types/mention';

type ActiveOrgRequest = {
  activeOrgId?: string | null;
};

function withActiveOrgHeader(
  request?: ActiveOrgRequest
): { headers: Record<string, string> } | undefined {
  const activeOrgId = request?.activeOrgId;
  const hasActiveOrgOverride =
    !!request && Object.prototype.hasOwnProperty.call(request, 'activeOrgId');

  if (!hasActiveOrgOverride) {
    return undefined;
  }

  return {
    headers: { 'X-Active-Org-Id': activeOrgId ?? '' },
  };
}

export interface MentionRelatedResourceItem {
  elementId: string;
  elementName: string;
  tagId?: string | null;
  tagLabel?: string | null;
  type: 'storyboard' | 'character' | 'location' | 'prop' | 'footage' | 'sound' | 'voice';
  resourceId: string;
  resourceUrl?: string | null;
  thumbnailUrl?: string | null;
  mimeType?: string | null;
  /** 文件大小（字节） */
  fileSize?: number | null;
  /** 宽度（像素） */
  width?: number | null;
  /** 高度（像素） */
  height?: number | null;
  /** 视频时长（秒） */
  duration?: number | null;
  /** 视频分辨率字符串（如 1920x1080） */
  resolution?: string | null;
  /** 资源格式（如 mp3 / wav / mp4） */
  format?: string | null;
  /** 视频帧率（fps） */
  frameRate?: number | null;
  /** 提交身份类型：1=资源图 2=分镜图片 3=分镜视频 4=附件 5=音效 6=配音 7=真人素材 */
  resourceType?: number | null;
  /** 人像资产类型：1=真人形象，2=虚拟形象 */
  humanAssetType?: number | null;
  /** 人像资产状态，用于展示认证标识 */
  humanAssetStatus?: number | null;
  /** 资源在其标签/方案内的版本序号（供拼接 @类型[名-标签V{seq}]） */
  versionSeq?: number | null;
}

export interface MentionRelatedResourcesResponse {
  resources: MentionRelatedResourceItem[];
}

/** 编辑页聚合候选：在 related-resources 项上扩展 plan 元数据与 video 类型 */
export interface EditorMentionCandidateItem extends Omit<MentionRelatedResourceItem, 'type'> {
  type: 'storyboard' | 'character' | 'location' | 'prop' | 'footage' | 'sound' | 'voice' | 'video';
  planId?: string | null;
  planName?: string | null;
}

export interface EditorMentionCandidatesResponse {
  resources: EditorMentionCandidateItem[];
}

export type EditorMentionGenType = 'image' | 'video' | 'sound' | 'voice' | 'text';

export interface EditorMentionCandidatesParams {
  genType: EditorMentionGenType;
  videoMode?: string | null;
  /** 缺省按 @ 面板候选解析；帧 picker 传 'frame'（仅帧模式可用，只回分镜 + 文件） */
  scope?: MentionCandidateScope | null;
}

export const aiConversationApi = {
  /**
   * 创建对话
   * POST /api/v1/ai/conversations
   */
  async createConversation(
    payload: ConversationCreateRequest,
    request?: ActiveOrgRequest
  ): Promise<ConversationDetail> {
    return apiClient.post(
      '/ai/conversations',
      {
        projectId: payload.projectId,
        targetType: payload.targetType,
        targetId: payload.targetId,
        tagId: payload.tagId ?? undefined,
        mediaType: payload.mediaType,
        title: payload.title,
      },
      withActiveOrgHeader(request)
    ) as Promise<ConversationDetail>;
  },

  /**
   * 获取会话列表
   * GET /api/v1/ai/conversations
   */
  async listConversations(
    params?: ConversationListParams,
    request?: ActiveOrgRequest
  ): Promise<PaginatedResponse<ConversationSummary>> {
    return apiClient.get('/ai/conversations', {
      params: {
        projectId: params?.projectId,
        targetType: params?.targetType,
        targetId: params?.targetId,
        tagId: params?.tagId ?? undefined,
        mediaType: params?.mediaType,
        status: params?.status,
        page: params?.page,
        pageSize: params?.pageSize,
      },
      ...withActiveOrgHeader(request),
    }) as Promise<PaginatedResponse<ConversationSummary>>;
  },

  /**
   * 发送消息
   * POST /api/v1/ai/conversations/:id/messages
   */
  async sendMessage(
    conversationId: string,
    payload: ConversationSendMessageRequest,
    request?: ActiveOrgRequest
  ): Promise<ConversationMessage> {
    return apiClient.post(
      `/ai/conversations/${conversationId}/messages`,
      {
        content: payload.content,
        generate: payload.generate,
      },
      withActiveOrgHeader(request)
    ) as Promise<ConversationMessage>;
  },

  /**
   * 获取消息列表
   * GET /api/v1/ai/conversations/:id/messages
   */
  async listMessages(
    conversationId: string,
    params?: ConversationMessageListParams,
    request?: ActiveOrgRequest
  ): Promise<PaginatedResponse<ConversationMessage>> {
    return apiClient.get(`/ai/conversations/${conversationId}/messages`, {
      params: {
        page: params?.page,
        pageSize: params?.pageSize,
        order: params?.order,
      },
      ...withActiveOrgHeader(request),
    }) as Promise<PaginatedResponse<ConversationMessage>>;
  },

  /**
   * 批量获取多个会话的消息列表
   * POST /api/v1/ai/conversations/messages/batch
   */
  async listMessagesBatch(
    params: ConversationMessagesBatchParams,
    request?: ActiveOrgRequest
  ): Promise<ConversationMessagesBatchResponse> {
    return apiClient.post(
      '/ai/conversations/messages/batch',
      {
        conversationIds: params.conversationIds,
        page: params.page,
        pageSize: params.pageSize,
        order: params.order,
      },
      withActiveOrgHeader(request)
    ) as Promise<ConversationMessagesBatchResponse>;
  },

  /**
   * 触发 AI 生成
   * POST /api/v1/ai/conversations/:id/generate
   */
  async triggerGenerate(
    conversationId: string,
    payload: ConversationTriggerGenerateRequest,
    request?: ActiveOrgRequest
  ): Promise<ConversationGenerateResult> {
    return apiClient.post(
      `/ai/conversations/${conversationId}/generate`,
      {
        messageId: payload.messageId,
        generationType: payload.generationType,
        params: payload.params,
      },
      withActiveOrgHeader(request)
    ) as Promise<ConversationGenerateResult>;
  },

  /**
   * 触发 Midjourney 四宫格图片拆分
   * POST /api/v1/ai/conversations/:id/messages/:messageId/midjourney/separate
   */
  async submitMidjourneySeparateAction(
    conversationId: string,
    messageId: string,
    index: number
  ): Promise<ConversationMidjourneySeparateResult> {
    return apiClient.post(
      `/ai/conversations/${conversationId}/messages/${messageId}/midjourney/separate`,
      { index }
    ) as Promise<ConversationMidjourneySeparateResult>;
  },

  /**
   * 获取可提及资源列表
   * GET /api/v1/ai/conversations/:id/mentionable-resources
   */
  async getMentionableResources(
    conversationId: string,
    params?: { type?: MentionResourceType; search?: string },
    request?: ActiveOrgRequest
  ): Promise<MentionableResourcesResponse> {
    return apiClient.get(`/ai/conversations/${conversationId}/mentionable-resources`, {
      params: {
        resourceType: params?.type,
        search: params?.search,
      },
      ...withActiveOrgHeader(request),
    }) as Promise<MentionableResourcesResponse>;
  },

  /**
   * 获取分镜相关可提及资源（新接口）
   * GET /api/v1/ai/conversations/storyboards/{storyboard_id}/related-resources
   */
  /**
   * 更新会话（标题、状态）
   * PATCH /api/v1/ai/conversations/:id
   */
  async updateConversation(
    conversationId: string,
    payload: { title?: string; status?: number },
    request?: ActiveOrgRequest
  ): Promise<ConversationDetail> {
    return apiClient.patch(
      `/ai/conversations/${conversationId}`,
      payload,
      withActiveOrgHeader(request)
    );
  },

  /**
   * 删除会话
   * DELETE /api/v1/ai/conversations/:id
   */
  async deleteConversation(conversationId: string, request?: ActiveOrgRequest): Promise<void> {
    await apiClient.delete(`/ai/conversations/${conversationId}`, withActiveOrgHeader(request));
  },

  async getStoryboardRelatedResources(
    storyboardId: string,
    request?: ActiveOrgRequest
  ): Promise<MentionRelatedResourcesResponse> {
    const data = (await apiClient.get(
      `/ai/conversations/storyboards/${storyboardId}/related-resources`,
      withActiveOrgHeader(request)
    )) as Partial<MentionRelatedResourcesResponse>;

    return {
      resources: Array.isArray(data.resources) ? data.resources : [],
    };
  },

  /**
   * 编辑页 @ 候选聚合接口（按 genType/videoMode 服务端硬过滤，扁平列表）
   * GET /api/v1/ai/conversations/storyboards/{storyboard_id}/mention-candidates
   */
  async getEditorMentionCandidates(
    storyboardId: string,
    params: EditorMentionCandidatesParams,
    request?: ActiveOrgRequest
  ): Promise<EditorMentionCandidatesResponse> {
    const data = (await apiClient.get(
      `/ai/conversations/storyboards/${storyboardId}/mention-candidates`,
      {
        params: {
          genType: params.genType,
          ...(params.genType === 'video' && params.videoMode
            ? { videoMode: params.videoMode }
            : {}),
          ...(params.scope ? { scope: params.scope } : {}),
        },
        ...withActiveOrgHeader(request),
      }
    )) as Partial<EditorMentionCandidatesResponse>;

    return {
      resources: Array.isArray(data.resources) ? data.resources : [],
    };
  },

  /**
   * 生成 AI 提示词（3 条）
   * POST /api/v1/ai/conversations/:id/generate-prompt
   */
  async generatePrompt(
    conversationId: string,
    payload: GeneratePromptRequest,
    request?: ActiveOrgRequest
  ): Promise<GeneratePromptResponse> {
    return apiClient.post(
      `/ai/conversations/${conversationId}/generate-prompt`,
      {
        targetType: payload.targetType,
        targetId: payload.targetId,
        ...(payload.elementTagId ? { elementTagId: payload.elementTagId } : {}),
      },
      withActiveOrgHeader(request)
    ) as Promise<GeneratePromptResponse>;
  },

  /**
   * 预估本次生成消耗词元
   * POST /api/v1/ai/conversations/:id/estimate-credits
   */
  async estimateCredits(
    conversationId: string,
    params: {
      modelName: string;
      generationType: string;
      aiParams: {
        n?: number;
        size?: string;
        mode?: string;
        generateAudio?: boolean;
        duration?: number;
      };
    },
    request?: ActiveOrgRequest
  ): Promise<number> {
    const res = (await apiClient.post(
      `/ai/conversations/${conversationId}/estimate-credits`,
      {
        content: 'x',
        generate: {
          modelName: params.modelName,
          generationType: params.generationType,
          aiParams: params.aiParams,
        },
      },
      withActiveOrgHeader(request)
    )) as { estimatedCredits: number };
    return res.estimatedCredits;
  },

  /**
   * 撤回会话生成消息
   * POST /api/v1/ai/conversations/messages/withdraw
   */
  async withdrawMessage(
    payload: { conversationId: string; messageId: string },
    request?: ActiveOrgRequest
  ): Promise<ConversationMessageWithdrawResult> {
    return apiClient.post(
      '/ai/conversations/messages/withdraw',
      {
        conversationId: payload.conversationId,
        messageId: payload.messageId,
      },
      withActiveOrgHeader(request)
    ) as Promise<ConversationMessageWithdrawResult>;
  },
};
